'use client';

import * as React from 'react';
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from '@mui/material';
import {
  CssBaseline,
} from '@mui/material';

const createMuiTheme = () => {
  return createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: 'hsl(var(--primary))',
        contrastText: 'hsl(var(--primary-foreground))',
      },
      secondary: {
        main: 'hsl(var(--secondary))',
        contrastText: 'hsl(var(--secondary-foreground))',
      },
      background: {
        default: 'hsl(var(--background))',
        paper: 'hsl(var(--card))',
      },
      text: {
        primary: 'hsl(var(--foreground))',
        secondary: 'hsl(var(--muted-foreground))',
      },
      error: {
        main: 'hsl(var(--destructive))',
        contrastText: 'hsl(var(--destructive-foreground))',
      },
      divider: 'hsl(var(--border))',
      action: {
        hover: 'hsl(var(--accent))',
        selected: 'hsl(var(--accent))',
        disabled: 'hsl(var(--muted))',
        disabledBackground: 'hsl(var(--muted))',
      },
    },
    typography: {
      fontFamily: 'var(--font-montserrat), var(--font-inter), sans-serif',
      h1: {
        fontFamily: 'var(--font-montserrat), sans-serif',
        fontWeight: 600,
      },
      h2: {
        fontFamily: 'var(--font-montserrat), sans-serif',
        fontWeight: 600,
      },
      h3: {
        fontFamily: 'var(--font-montserrat), sans-serif',
        fontWeight: 600,
      },
      h4: {
        fontFamily: 'var(--font-montserrat), sans-serif',
        fontWeight: 600,
      },
      h5: {
        fontFamily: 'var(--font-montserrat), sans-serif',
        fontWeight: 600,
      },
      h6: {
        fontFamily: 'var(--font-montserrat), sans-serif',
        fontWeight: 600,
      },
      button: {
        fontFamily: 'var(--font-montserrat), sans-serif',
        fontWeight: 500,
      },
      caption: {
        fontFamily: 'var(--font-montserrat), sans-serif',
      },
      overline: {
        fontFamily: 'var(--font-montserrat), sans-serif',
      },
    },
    shape: {
      borderRadius: 8, // Matches Tailwind's default border radius
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: 'hsl(var(--background))',
            color: 'hsl(var(--foreground))',
          },
        },
      },
      // Override MUI components to use Tailwind colors
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 'var(--radius)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 'var(--radius)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 'var(--radius)',
            backgroundColor: 'hsl(var(--card))',
            color: 'hsl(var(--card-foreground))',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: 'hsl(var(--card))',
            color: 'hsl(var(--card-foreground))',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 'var(--radius)',
            backgroundColor: 'hsl(var(--card))',
            color: 'hsl(var(--card-foreground))',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 'var(--radius)',
            backgroundColor: 'hsl(var(--popover))',
            color: 'hsl(var(--popover-foreground))',
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: 'var(--radius)',
            backgroundColor: 'hsl(var(--popover))',
            color: 'hsl(var(--popover-foreground))',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: 'hsl(var(--popover))',
            color: 'hsl(var(--popover-foreground))',
            borderRadius: 'var(--radius)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 'var(--radius)',
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            borderRadius: 'var(--radius)',
            backgroundColor: 'hsl(var(--card))',
            color: 'hsl(var(--card-foreground))',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: 'hsl(var(--primary))',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            '&.Mui-selected': {
              color: 'hsl(var(--primary))',
            },
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: 'hsl(var(--muted))',
            '&::after': {
              background: 'linear-gradient(90deg, transparent, hsl(var(--muted-foreground) / 0.1), transparent)',
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            '& .MuiSelect-select': {
              color: 'hsl(var(--foreground))',
            },
            '& .MuiSelect-select.Mui-disabled': {
              color: 'hsl(var(--muted-foreground))',
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: 'hsl(var(--foreground)) !important',
            '&:hover': {
              backgroundColor: 'hsl(var(--muted)) !important',
              color: 'hsl(var(--foreground)) !important',
            },
            '&.Mui-focused': {
              backgroundColor: 'hsl(var(--muted)) !important',
              color: 'hsl(var(--foreground)) !important',
            },
            '&.Mui-selected': {
              backgroundColor: 'hsl(var(--muted)) !important',
              color: 'hsl(var(--foreground)) !important',
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-input': {
              color: 'hsl(var(--foreground))',
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'hsl(var(--muted-foreground))',
              opacity: 1,
            },
          },
          option: {
            color: 'hsl(var(--foreground)) !important',
            '&:hover': {
              backgroundColor: 'hsl(var(--muted)) !important',
              color: 'hsl(var(--foreground)) !important',
            },
            '&.Mui-focused': {
              backgroundColor: 'hsl(var(--muted)) !important',
              color: 'hsl(var(--foreground)) !important',
            },
            '&.Mui-selected': {
              backgroundColor: 'hsl(var(--muted)) !important',
              color: 'hsl(var(--foreground)) !important',
            },
          },
        },
      },
    },
  });
};

export default function ThemeProvider({ children }: { children: React.ReactNode }) {
  const muiTheme = React.useMemo(() => createMuiTheme(), []);

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
}
